import {
  ResizableProvider,
  ResizeHandle as BaseResizeHandle,
  ResizeHandleProvider,
  useResizable,
} from '@platejs/resizable'
import cn from 'classnames'
import { type ComponentProps, type FC } from 'react'
import { useReadOnly, useSelected } from 'slate-react'
import { Block } from './block.js'
import type { BlockProps } from './block.js'
import styles from './resize-block.module.css'
import { useResizableBlockState } from './use-resizable-block-state.js'

export interface ResizableBlockProps extends BlockProps {
  element: BlockProps['element'] & { width?: string | number }
  align?: 'left' | 'center' | 'right'
  minWidth?: number | string
  maxWidth?: number | string
}

const LeftHandle = ({ focused, ...props }: ComponentProps<typeof BaseResizeHandle> & { focused: boolean }) => (
  <BaseResizeHandle
    {...props}
    className={cn(styles.resizeHandle, styles['resizeHandle--left'], focused && styles['resizeHandle--focused'])}
  />
)

const RightHandle = ({ focused, ...props }: ComponentProps<typeof BaseResizeHandle> & { focused: boolean }) => (
  <BaseResizeHandle
    {...props}
    className={cn(styles.resizeHandle, styles['resizeHandle--right'], focused && styles['resizeHandle--focused'])}
  />
)

const ResizableBlockInner: FC<ResizableBlockProps> = props => {
  const { children, align, minWidth = 100, maxWidth = '100%', ...restProps } = props
  const focused = useSelected()
  const readOnly = useReadOnly()

  const state = useResizableBlockState({
    readOnly,
    minWidth,
    maxWidth,
    align,
  })
  const { wrapperRef, wrapperProps, props: rProps, context } = useResizable(state)

  return (
    <Block {...restProps} border={false}>
      <div
        ref={wrapperRef}
        {...wrapperProps}
        className={styles.wrapper}
        style={{ justifyContent: align, ...wrapperProps.style }}
        contentEditable={false}
      >
        <div {...rProps}>
          <ResizeHandleProvider onResize={context.onResize}>
            {!readOnly ? <LeftHandle focused={focused} options={{ direction: 'left' }} /> : null}
            {children}
            {!readOnly ? <RightHandle focused={focused} options={{ direction: 'right' }} /> : null}
          </ResizeHandleProvider>
        </div>
      </div>
    </Block>
  )
}

export const ResizableBlock: FC<ResizableBlockProps> = props => (
  <ResizableProvider>
    <ResizableBlockInner {...props} />
  </ResizableProvider>
)

export default ResizableBlock
